import React, { useRef, useState } from 'react';
import { View, TouchableOpacity, Pressable, ScrollView } from 'react-native';
import Animated, { FadeIn, FadeOut, useAnimatedStyle } from 'react-native-reanimated';
import { brand, neutrals } from 'src/theme/colors';
import { ICON_NAMES } from '../../../icon/icon.helper';
import { Icon } from '../../../icon/Icon';
import TextLato from '../../../text/text-lato/TextLato';
import { styles } from './GridItemContentHeader.styles';
import { useGridItemContentHeaderLogic } from './GridItemContentHeader.logic';
import { GridItemContentHeaderProps } from './GridItemContentHeader.types';
import { isWeb } from 'src/_shared/utils/isWeb';
import { observer } from 'mobx-react-lite';
import { useResponsiveScale } from 'src/contexts/ResponsiveScaleContext';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { STACKS_ANIMATION_MS } from 'src/constants/expandableGroup.constants';
import { ProductOptionsMobile } from '@photosi/rn-components/product-options-mobile/ProductOptionsMobile';
import { ProductOptionsMobilePosition } from '@photosi/rn-components/product-options-mobile/ProductOptionsMobile.types';
import { useOnClickOutside } from 'src/hooks/useOnClickOutside';
import { productTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { LookupDropdownItem } from './LookupDropdownItem';
import { LookupCodes } from 'src/constants/lookup.constants';
import { isHappyCaseOrPhotoKit } from 'src/helpers/group.helper';
import { GROUP_CODES } from 'src/constants/code.constants';

/**
 * GridItemContentHeader Component
 *
 * This component renders a title bar with optional check icon on the left
 * and two action icons on the right with animated hover effects.
 *
 * Props:
 * - text: The title text to display
 * - edit: Function called when edit icon is clicked
 * - duplicate: Function called when duplicate icon is clicked
 * - isSelected: Whether to show the check icon
 * - isLookupOpen: Whether the lookup view is open
 *
 * Animations:
 * - Animated hover effect for icons on web (shows text above icons)
 * - FadeIn/FadeOut animations for hover text
 */
export const GridItemContentHeader: React.FC<GridItemContentHeaderProps> = observer(
  ({ textFormat, textPaper, onDuplicate, product, dropdownPosition, onShowOptionMobile }) => {
    const {
      i18n,
      isLookupHovered,
      isEditHovered,
      handleLookupMouseEnter,
      handleLookupMouseLeave,
      handleEditMouseEnter,
      handleEditMouseLeave,
      onPressEdit,
      isMultiphotoProductComplete,
      isProjectAddedToCart,
      syncProductsOptions,
      getProductOptions,
      applyLookupToProductGroup,
      setProductLookup,
      productLookup,
    } = useGridItemContentHeaderLogic(product);

    const { responsiveScaleSV } = useResponsiveScale();
    const { isDesktop } = useResponsiveScreen();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [isDropdownLookupOpen, setIsDropdownLookupOpen] = useState(false);
    const dropdownGroupRef = useRef(null);

    useOnClickOutside([dropdownRef, dropdownGroupRef], () => {
      setIsDropdownOpen(false);
      setIsDropdownLookupOpen(false);
    });

    const maxWidthAnimatedStyle = useAnimatedStyle(() => {
      return {
        width: product.preview.viewBox.width * responsiveScaleSV.value,
      };
    }, [product.preview.viewBox]);
    const isSelected = product.isSelected;

    const productOptions = getProductOptions(productLookup);
    const isHappyCase = isHappyCaseOrPhotoKit(product.groupCode as GROUP_CODES);

    const openProductOptions = (type: LookupCodes) => {
      if (!productOptions) syncProductsOptions();
      setProductLookup(type);
      setIsDropdownLookupOpen(true);
    };

    const [rightWidth, setRightWidth] = useState(0);
    const TWO_ICONS_WIDTH = 32 * 2;
    const showOverflowIcon = rightWidth > TWO_ICONS_WIDTH;

    return (
      <Animated.View style={[styles.container, maxWidthAnimatedStyle]}>
        <View style={styles.leftSection}>
          <View style={[styles.checkIconContainer, isSelected && styles.checkIconContainerSelected]}>
            <View style={styles.checkIcon}>
              {isSelected && <Icon name={ICON_NAMES.Check} color={neutrals.white} />}
            </View>
          </View>
          <View style={styles.sectionWrapper}>
            {textFormat && (
              <Pressable
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                onPress={() => !isHappyCase && openProductOptions(LookupCodes.FORMAT)}
              >
                <TextLato style={[!isHappyCase && styles.dottedLine, styles.text]} numberOfLines={1}>
                  {textFormat}
                </TextLato>
                {textPaper && (
                  <TextLato style={styles.textDot} numberOfLines={1}>
                    {' · '}
                  </TextLato>
                )}
              </Pressable>
            )}
            {textPaper && (
              <Pressable style={styles.dottedLine} onPress={() => openProductOptions(LookupCodes.PAPER)}>
                <TextLato style={styles.text} numberOfLines={1}>
                  {textPaper}
                </TextLato>
              </Pressable>
            )}
          </View>
        </View>

        {isDropdownLookupOpen && productOptions && (
          <Animated.View
            ref={dropdownGroupRef}
            entering={FadeIn.duration(STACKS_ANIMATION_MS / 2)}
            exiting={FadeOut.duration(STACKS_ANIMATION_MS / 2)}
            style={styles.dropdownLookupContainer}
          >
            <ScrollView style={styles.scrollViewVertical}>
              {productOptions?.map(item => (
                <LookupDropdownItem
                  key={item.productOptionChoiceCode}
                  item={item}
                  showPrice={productLookup === LookupCodes.FORMAT}
                  onPress={item => {
                    setIsDropdownLookupOpen(false);
                    applyLookupToProductGroup(item);
                  }}
                />
              ))}
            </ScrollView>
          </Animated.View>
        )}

        {/* Right section with action icons */}
        <View
          style={[styles.rightSection]}
          onLayout={e => {
            setRightWidth(e.nativeEvent.layout.width);
          }}
        >
          {/* Duplicate icon with hover animation */}
          {isDesktop && !isProjectAddedToCart && showOverflowIcon && (
            <View style={styles.iconButtonContainer}>
              <TouchableOpacity
                style={[styles.iconButton]}
                onPress={() => {
                  amplitude.trackEvent(AnalyticsEvents.personalize.duplicatePhotoClick);
                  onDuplicate();
                }}
                {...(isWeb() && {
                  onMouseEnter: handleLookupMouseEnter,
                  onMouseLeave: handleLookupMouseLeave,
                })}
                testID={productTestID(product.id, TestIDs.DUPLICATE)}
              >
                {isLookupHovered && (
                  <Animated.View
                    entering={FadeIn.duration(200)}
                    exiting={FadeOut.duration(200)}
                    style={styles.hoverTextContainer}
                    pointerEvents="none"
                  >
                    <TextLato style={styles.hoverText}>{i18n.t('actions:duplicate')}</TextLato>
                  </Animated.View>
                )}
                <View style={styles.icon}>
                  <Icon name={ICON_NAMES.Copy} color={brand.main.water} />
                </View>
              </TouchableOpacity>
            </View>
          )}

          {/* Edit icon with hover animation */}
          {isDesktop && showOverflowIcon && (
            <View style={styles.iconButtonContainer}>
              <TouchableOpacity
                style={[styles.iconButton]}
                onPress={() => onPressEdit(isMultiphotoProductComplete)}
                {...(isWeb() && {
                  onMouseEnter: handleEditMouseEnter,
                  onMouseLeave: handleEditMouseLeave,
                })}
                testID={productTestID(product.id, TestIDs.EDIT)}
              >
                {isEditHovered && (
                  <Animated.View
                    entering={FadeIn.duration(200)}
                    exiting={FadeOut.duration(200)}
                    style={styles.hoverTextContainer}
                    pointerEvents="none"
                  >
                    <TextLato style={styles.hoverText}>{i18n.t('actions:edit')}</TextLato>
                  </Animated.View>
                )}
                <View style={styles.icon}>
                  <Icon name={ICON_NAMES.Edit} color={brand.main.water} />
                </View>
              </TouchableOpacity>
            </View>
          )}

          {(!isDesktop || !showOverflowIcon) && (
            <View ref={dropdownRef}>
              <Pressable
                style={[styles.selectButton, isDropdownOpen && styles.selectedButton]}
                onPress={() => {
                  setIsDropdownOpen(!isDropdownOpen);
                  !isDesktop && onShowOptionMobile && onShowOptionMobile();
                }}
              >
                <View style={styles.icon}>
                  <Icon name={ICON_NAMES.More} color={brand.main.water} />
                </View>
              </Pressable>
              {(!isDesktop || !showOverflowIcon) && isDropdownOpen && (
                <Animated.View
                  entering={FadeIn.duration(STACKS_ANIMATION_MS / 2)}
                  exiting={FadeOut.duration(STACKS_ANIMATION_MS / 2)}
                  style={[
                    styles.dropdownContainer,
                    dropdownPosition === ProductOptionsMobilePosition.RIGHT ? { left: 10 } : { right: 10 },
                  ]}
                >
                  <ProductOptionsMobile
                    product={product}
                    containerStyle={isDesktop && { width: 150 }}
                    avoidCounterAction={isDesktop}
                  />
                </Animated.View>
              )}
            </View>
          )}
        </View>
      </Animated.View>
    );
  },
);
